import type { WorkOrderResponse } from '@palaccount/contracts';

import { formatQty } from '@/lib/format';

/**
 * PART 9's small pieces of presentation arithmetic, in one place and pure, so
 * they can be checked against the spec's own worked figures without rendering
 * anything.
 *
 * Nothing here computes MONEY. §9.4 gives the module exactly one cost
 * authority — the shared costing service on the server — and BR-MFG-01-12 says
 * "no cost field on this screen may be editable". These functions decide what
 * COLOUR a number is and which stage a stepper circle is in; the numbers
 * themselves arrive already made.
 */

/** MFG-02 §5.1 field 7 — the five stepper stages, left to right. */
export const WORK_ORDER_STAGES = [
  'draft',
  'scheduled',
  'inprogress',
  'completed',
  'closed',
] as const;
export type WorkOrderStage = (typeof WORK_ORDER_STAGES)[number];

/**
 * Field 7's three circle states, verbatim: "A finished stage shows a green
 * circle with a tick. The current stage shows a blue circle with its number. A
 * stage not yet reached shows an empty grey circle with no number."
 *
 * §7's two exception statuses are deliberately NOT stages: "They never appear
 * as a stepper circle. When a job is in either, the stepper keeps showing the
 * stage it reached and the amber banner above it carries the exception." So
 * On Hold reads as the stage it was held from, and Cancelled keeps whatever it
 * had reached.
 */
export function stepperState(
  status: WorkOrderResponse['status'],
  stage: WorkOrderStage,
): 'done' | 'current' | 'pending' {
  const reached =
    status === 'onhold'
      ? 'inprogress'
      : status === 'cancelled'
        ? 'draft'
        : (status as WorkOrderStage);
  const at = WORK_ORDER_STAGES.indexOf(reached);
  const index = WORK_ORDER_STAGES.indexOf(stage);
  if (index < at) return 'done';
  if (index === at) return 'current';
  return 'pending';
}

/**
 * MFG-04 §5.6's colour band: "0 % to 2 % grey · over 2 % up to 5 % amber ·
 * over 5 % red", measured on the ABSOLUTE percentage, so using ten more than
 * the recipe said and ten fewer are equally worth looking at.
 */
export function varianceBandClass(band: string): string {
  switch (band) {
    case 'investigate':
      return 'text-red-600 dark:text-red-400';
    case 'watch':
      return 'text-amber-700 dark:text-amber-400';
    default:
      return 'text-muted-foreground';
  }
}

/**
 * MFG-03 §5.8 field 33: "green at 85 % or below, amber above 85 % up to
 * 100 %, red above 100 %".
 */
export function capacityBarClass(band: string): string {
  switch (band) {
    case 'overloaded':
      return 'bg-red-500';
    case 'tight':
      return 'bg-amber-500';
    default:
      return 'bg-emerald-500';
  }
}

/**
 * Field 33 again: "The bar's fill is CLAMPED AT 100 % even when the text says
 * 110 %, because the track cannot show more." BR-MFG-03-12 says the same thing
 * from the other side — the percentage itself must be allowed to exceed 100.
 */
export function barWidth(percent: number): string {
  return `${Math.max(0, Math.min(100, percent))}%`;
}

/**
 * MFG-02 §5.6 field 43's sign convention, confirmed in the shipped code:
 * `actual − planned`, "RED WHEN POSITIVE (it cost more than planned) and green
 * when negative", with a leading `+`.
 */
export function varianceTone(variance: string): 'over' | 'under' | 'level' {
  const value = Number.parseFloat(variance);
  if (Number.isNaN(value) || Math.abs(value) < 0.005) return 'level';
  return value > 0 ? 'over' : 'under';
}

/** The same figure as the user reads it: `+3.55`, `−1.20`, `0.00`. */
export function signedAmount(amount: string): string {
  const tone = varianceTone(amount);
  return tone === 'over' ? `+${amount}` : amount;
}

/**
 * MFG-02 §6: "Mark Complete … Its label shows the shortfall:
 * `Mark Complete (Remaining 0)`." The remaining figure arrives floored at zero
 * from the server (field 36), so producing over target still reads 0 rather
 * than a negative.
 */
export function completeLabel(label: string, remaining: string): string {
  return `${label} (${trimQty(remaining)})`;
}

/**
 * '210.000' reads as '210'; '1.020' keeps what it needs.
 *
 * NOT FOR A QUANTITY DISPLAY. `N-4` asks for "the number of decimals the
 * item's unit of measure allows", and trimming is the opposite instruction —
 * a component called for in kilograms must print `1.020`, because `1.02` in a
 * 3-decimal unit claims a precision the recipe did not state. MFG-01's recipe
 * grid, MFG-02's job and MFG-04's ledger now go through `formatQty` and
 * `<Qty>` instead.
 *
 * `QTY_WITHOUT_UNIT` IS NOW EMPTY — the planning board and the work-order
 * register carry `unitSymbol`/`unitPrecision` on their wires and render through
 * `<Qty>` and `formatQty` — so this has NO quantity display left. What survives
 * is what it was always right for and nothing else:
 *
 *  - an EDITABLE box's prefill (`bom-page.tsx`'s component grid, the quick
 *    completion's good and scrap boxes): making somebody delete "0.000" before
 *    they can type is not what `N-4` asks for;
 *  - `completeLabel`, whose figure sits inside a sentence MFG-02 §6 writes out
 *    in full — `Mark Complete (Remaining 0)`;
 *  - `yieldPct`, which is a PERCENTAGE and has no unit of measure at all.
 *
 * A new call site for a read-only quantity is a defect, and the doc block on
 * `QTY_WITHOUT_UNIT` is where it gets written down before it gets fixed.
 */
export function trimQty(qty: string): string {
  if (!qty.includes('.')) return qty;
  const trimmed = qty.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed === '' || trimmed === '-' ? '0' : trimmed;
}

/**
 * MFG-01 §5.3: workstation values are CODES (`CNC-01`, `Bench-03`), and
 * BR-MFG-01-11 matches them exactly and case-sensitively. §15 calls the
 * consequence "the most likely real-world failure on this screen":
 * `Assembly Bench 2` and `Assembly Bench 2 ` with a trailing space "produce
 * two capacity rows, each half-loaded, and no overload warning".
 *
 * A space nobody can see is what makes that failure invisible, so the ENDS of
 * the string are marked when they are blank. The middle is left alone: a
 * workstation named with an ordinary internal space is not a typo.
 */
export function visibleWorkstation(workstation: string | null): string {
  if (workstation === null) return '—';
  if (workstation === workstation.trim()) return workstation;
  const leading = workstation.length - workstation.trimStart().length;
  const trailing = workstation.length - workstation.trimEnd().length;
  return `${'␣'.repeat(leading)}${workstation.trim()}${'␣'.repeat(trailing)}`;
}

/**
 * MFG-04 §5.4 field 18: the ledger quantity is SIGNED and rendered with a
 * leading `+` for an issue — "this is the clearest evidence that quantities
 * here can be negative".
 *
 * `precision` is REQUIRED, and it is `consumption_voucher_lines.unit_id`'s —
 * the unit the issue was WRITTEN in (`BR-MD-03-04d`), not the item's stock
 * unit. It used to trim, which is `N-4` backwards; an optional parameter here
 * would just let the next caller keep doing that.
 *
 * The `−` is U+2212, not a hyphen: this column is read as a signed ledger and
 * the true minus lines up with the `+` in a tabular face.
 */
export function ledgerQty(qty: string, precision: number): string {
  const written = formatQty(qty, precision);
  return written.startsWith('-') ? written.replace('-', '−') : `+${written}`;
}
